import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NodeTypeDefinition } from '../../../../../../src/types/workflow';

export const NODE_TYPES_SLICE_FEATURE_KEY = 'nodeTypes';

export interface NodeTypesState {
  list: NodeTypeDefinition[];
  isLoading: boolean;
}

const defaultNodeTypes: NodeTypeDefinition[] = [
  {
    type: 'outputNode',
    displayName: 'Output',
    icon: 'mdi:export',
    category: 'System',
    color: '#F59E0B',
    description: 'Define the final output of this workflow. Add named fields and bind each one to an upstream node\'s output. The result is a structured object shown in the Converse section.',
    isSystemNode: false,
    configSchema: [],
  },
  {
    type: 'end',
    displayName: 'End',
    icon: 'mdi:flag',
    category: 'System',
    color: '#F59E0B',
    description: 'The termination point of your workflow. Workflows complete execution when reaching this node. Cannot have outgoing connections.',
    isSystemNode: true,
    configSchema: [
      { name: 'outputVariables', label: 'Final Output Variables', type: 'json', placeholder: '{"result": "{{someVar}}"}' },
    ],
  },
  // Triggers — workflow entry points
  {
    type: 'scheduleTrigger',
    displayName: 'Schedule Trigger',
    icon: 'mdi:calendar-clock',
    category: 'Triggers',
    color: '#F59E0B',
    description: 'Triggers the workflow on a recurring schedule using a cron expression.',
    configSchema: [
      { name: 'cronExpression', label: 'Cron Expression', type: 'string', placeholder: '0 9 * * *' },
      { name: 'timezone', label: 'Timezone', type: 'string', placeholder: 'UTC' },
    ],
  },
  {
    type: 'webhookTrigger',
    displayName: 'Webhook Trigger',
    icon: 'mdi:webhook',
    category: 'Triggers',
    color: '#10B981',
    description: 'Triggers the workflow when an HTTP request is received at the webhook URL.',
    configSchema: [
      { name: 'method', label: 'HTTP Method', type: 'enum', options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'], defaultValue: 'POST' },
    ],
  },
  {
    type: 'emailTrigger',
    displayName: 'Email Received',
    icon: 'mdi:email-receive-outline',
    category: 'Triggers',
    color: '#0078D4',
    description: 'Triggers the workflow when an email is received in Microsoft Outlook. Uses the langgenius/outlook_trigger Dify plugin.',
    configSchema: [
      { name: 'senderEmailFilter', label: 'Sender Email Filter', type: 'string', placeholder: 'foo@example.com, bar@example.com (leave empty for any sender)' },
      { name: 'subscriptionId', label: 'Outlook Subscription ID', type: 'string', placeholder: 'Auto-filled after connecting in Dify' },
      { name: 'connectionStatus', label: 'Connection Status', type: 'enum', options: ['connected', 'disconnected'], defaultValue: 'disconnected' },
    ],
  },
  // AI nodes
  {
    type: 'llmAgentNode',
    displayName: 'LLM Agent',
    icon: 'mdi:brain',
    category: 'AI',
    color: '#7C3AED',
    description:
      'Run an LLM with custom instructions and context from previous nodes. Model and strategy are configurable per node.',
    configSchema: [
      { name: 'instructions', label: 'Instructions', type: 'textarea', placeholder: 'You are a helpful assistant...' },
      { name: 'userPrompt', label: 'User Message', type: 'textarea', placeholder: 'Optional user message template' },
      { name: 'provider', label: 'Provider', type: 'enum', options: ['openai', 'anthropic', 'google', 'mistral', 'cohere'], defaultValue: 'openai' },
      { name: 'modelName', label: 'Model', type: 'string', placeholder: 'gpt-4o' },
      { name: 'temperature', label: 'Temperature', type: 'number', defaultValue: 0.7 },
      { name: 'maxTokens', label: 'Max Tokens', type: 'number', defaultValue: 1024 },
    ],
  },
  // Transform
  {
    type: 'code',
    displayName: 'Code Transform',
    icon: 'mdi:code-braces',
    category: 'Logic',
    color: '#10B981',
    description: 'Transform data using Python or JavaScript. Bind inputs from upstream nodes, write transformation code, and declare output variables for downstream nodes.',
    configSchema: [],
  },
  // Control-flow
  {
    type: 'loopNode',
    displayName: 'Loop / Iterate',
    icon: 'mdi:repeat',
    category: 'Logic',
    color: '#D97706',
    description:
      'Iterate over each item in a list or repeat N times. Connect body nodes via the "Body" handle; connect the next step via the "Done" handle.',
    configSchema: [
      { name: 'loopType',      label: 'Loop Type',              type: 'enum', options: ['list', 'count'], defaultValue: 'list' },
      { name: 'iteratorInput', label: 'Iterator Input',         type: 'string', placeholder: '$prevNode.items' },
      { name: 'countValue',    label: 'Count',                  type: 'number', defaultValue: 3 },
      { name: 'maxIterations', label: 'Max Iterations',         type: 'number', defaultValue: 10 },
      { name: 'outputField',   label: 'Output Field to Collect', type: 'string', placeholder: 'output' },
    ],
  },
  {
    type: 'ifElse',
    displayName: 'IF/ELSE',
    icon: 'mdi:code-braces',
    category: 'Logic',
    color: '#06B6D4',
    description: 'Conditional branching logic. Execute different paths based on a condition. Connect the true path to the top output and false path to the bottom output.',
    configSchema: [
      { name: 'condition', label: 'Condition', type: 'string', required: true, placeholder: '{{input.value}} > 10' },
    ],
  },
  // AI integration nodes
  {
    type: 'advisorNode',
    displayName: 'Advisor Node',
    icon: 'fluent:glasses-28-regular',
    category: 'Advisors',
    color: '#2563EB',
    description:
      'Trigger an AI advisor from your workspace to provide consultation or analysis as part of this workflow.',
    configSchema: [
      {
        name: 'advisorId',
        label: 'Advisor ID',
        type: 'string',
        required: true,
        placeholder: 'Select advisor when adding the node or enter an advisor ID',
      },
      {
        name: 'advisorName',
        label: 'Advisor Name',
        type: 'string',
        placeholder: 'Display name of the advisor used for this step',
      },
      {
        name: 'user_input',
        label: 'User Input',
        type: 'textarea',
        required: true,
        placeholder: 'Type the query here, e.g. "where should I store the TL130 operator\'s manual?" or use $prevNode.output to pass output from a previous node.',
      },
    ],
  },
  {
    type: 'agentNode',
    displayName: 'Agent Node',
    icon: 'mdi:robot',
    category: 'Agents',
    color: '#9333EA',
    description:
      'Trigger an AI agent from your workspace to execute an action as part of this workflow. The agent and payload can be configured per node.',
    configSchema: [
      {
        name: 'agentId',
        label: 'Agent ID',
        type: 'string',
        required: true,
        placeholder: 'Select agent when adding the node or enter an agent ID',
      },
      {
        name: 'agentName',
        label: 'Agent Name',
        type: 'string',
        placeholder: 'Display name of the agent used for this step',
      },
      {
        name: 'user_input',
        label: 'User Input',
        type: 'textarea',
        required: true,
        placeholder: 'Type the query here, e.g. "process claim for VIN ABC123" or use $prevNode.output to pass output from a previous node.',
      },
    ],
  },
  {
    type: 'saveToDbNode',
    displayName: 'Save to DB',
    icon: 'mdi:database-arrow-up',
    category: 'Logic',
    color: '#059669',
    description: 'Saves the current workflow run and all node outputs to the Circuitry database. When exported to Dify, generates a pre-configured HTTP request node with the full payload.',
    configSchema: [],
  },
  {
    type: 'httpRequest',
    displayName: 'HTTP Request',
    icon: 'mdi:web',
    category: 'Logic',
    color: '#0891B2',
    description: 'Make an HTTP request to any external API or webhook. Configure method, URL, headers, query params, and body. Supports template variables from upstream nodes.',
    configSchema: [],
  },
  {
    type: 'hitlNode',
    displayName: 'Human in the Loop',
    icon: 'mdi:account-check',
    category: 'Control',
    color: '#DC2626',
    description:
      'Governed decision checkpoint. Calls a Dify HITL workflow that evaluates prior node outputs and either auto-decides (approve/reject) or suspends the journey for human review with SLA tracking.',
    configSchema: [],
  },
];

const initialState: NodeTypesState = {
  list: defaultNodeTypes,
  isLoading: false,
};

export const nodeTypesSlice = createSlice({
  name: NODE_TYPES_SLICE_FEATURE_KEY,
  initialState,
  reducers: {
    setNodeTypes: (state, action: PayloadAction<NodeTypeDefinition[]>) => {
      state.list = action.payload;
    },
    addNodeType: (state, action: PayloadAction<NodeTypeDefinition>) => {
      state.list.push(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { setNodeTypes, addNodeType, setLoading: setNodeTypesLoading } = nodeTypesSlice.actions;

export default nodeTypesSlice.reducer;
